import type { ReactNode } from "react";

/**
 * Shared loading / load-failure presentation. Pair with `resolveLoadState`
 * (lib/load-state.ts): an "error" state renders this — never an empty state —
 * so the operator is not invited to create, connect or buy something that may
 * already exist.
 */
export function LoadingState({ label = "Loading…", compact = false }: { label?: string; compact?: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={compact ? "py-3 text-sm text-slate-500" : "flex min-h-40 items-center justify-center p-8 text-sm text-slate-600"}
    >
      {label}
    </div>
  );
}

export function LoadErrorState({
  title = "This could not be loaded",
  description = "We could not reach the server. Nothing has been changed — try again.",
  onRetry,
  compact = false,
  children,
}: {
  title?: string;
  description?: ReactNode;
  onRetry?: () => unknown;
  compact?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      role="alert"
      data-load-state="error"
      className={compact
        ? "rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        : "rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800"}
    >
      <p className="font-semibold text-red-900">{title}</p>
      {description ? <div className="mt-1 leading-6">{description}</div> : null}
      {onRetry ? (
        <button
          type="button"
          onClick={() => void onRetry()}
          className="mt-3 inline-flex min-h-9 items-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100"
        >
          Try again
        </button>
      ) : null}
      {children}
    </div>
  );
}
